import { Injectable } from '@angular/core';
import { MemoizedSelector, Store } from "@ngrx/store";
import { combineLatest, Observable, of, switchMap } from "rxjs";
import { filter, map, shareReplay, take } from "rxjs/operators";
import { AccessLevelEnum, IGetRolesResponse } from "../../main/pages/users/roles/roles.service";
import * as AuthSelectors from './../../store/selectors/auth.selectors';
import * as RolesSelectors from 'app/store/selectors/roles.selectors';
import * as CategoriesSelectors from 'app/store/selectors/categories.selector';
import { PermissionsEnum } from "./permissions.enum";
import * as UserPermissionsSelectors from "./store/permission.selectors";
import { UserSettingsFacade } from 'app/store/user-settings.facade';

@Injectable({
    providedIn: 'root'
})
export class PermissionsService {
    isTenantOwner$ = this.store.select(AuthSelectors.isTenantOwner);
    userRoles$ = this.store.select(AuthSelectors.userRoles);

    constructor(
        private store: Store,
        private _userSettingsFacade$: UserSettingsFacade
    ) { }


    

    /**
     * Retrieves the default user role from the list of user roles.
     * 
     * @returns {Observable<IGetRolesResponse>} An observable that emits the default user role.
     * 
     * The method filters the user roles to ensure there is at least one role,
     * takes the first emitted value, and maps it to find the role that is either
     * marked as default or predefined.
     */
    getUserDefaultRole(): Observable<IGetRolesResponse> {
        return this.userRoles$.pipe(
            filter(res => res?.length > 0),
            take(1),
            map(res => res?.find(x => x.default == true || x.predefined == true))
        );
    }


   


    /**
     * Checks if the current user has the specified permission.
     *
     * @param {PermissionsEnum} permission - The permission to check.
     * 
     * @description This method checks if the current user has the specified permission.
     * @returns {Observable<boolean>} An observable that emits `true` if the user has the permission, otherwise `false`.
     */
    hasPermission(permission: PermissionsEnum): Observable<boolean> {
        return this.isTenantOwner$.pipe(
            switchMap(isTenantOwner => isTenantOwner
                ? of(isTenantOwner)
                : this.store.select(UserPermissionsSelectors.hasPermission(permission)))
        );
    }



    /**
     * Retrieves the parent category IDs for a given set of category IDs.
     *
     * @param categoryIds - A single category ID or an array of category IDs.
     * @returns An Observable that emits an array of parent category IDs.
     */
    getParentCategoriesIds(categoryIds: number | number[]): Observable<number[]> {
        return this.store.select(CategoriesSelectors.getParentCategoriesIds(categoryIds));
    }

    


    /**
     * Checks if the user has at least one of the specified permissions.
     *
     * @param permissions - An array of permissions to check.
     * 
     * @description This method checks if the user has at least one of the specified permissions.
     * @returns An Observable that emits `true` if the user has at least one of the specified permissions, otherwise `false`.
     */
    hasSomePermission(permissions: PermissionsEnum[]): Observable<boolean> {
        return combineLatest(permissions.map(x => this.hasPermission(x)))
            .pipe(map((x: boolean[]) => x.some(Boolean)));
    }

    



    /**
     * Checks if a given role has a specific permission.
     *
     * This method first checks if the current user is a tenant owner. If the user is a tenant owner,
     * it returns an observable of `true`. Otherwise, it checks the store to see if the specified role
     * has the given permission.
     *
     * @param role - The role to check for the permission.
     * @param permission - The permission to check within the role.
     * @returns An observable that emits `true` if the role has the permission or if the user is a tenant owner,
     *          and `false` otherwise.
     */
    hasPermissionInRole(role: string, permission: string): Observable<boolean> {
        return this.isTenantOwner$.pipe(
            switchMap(isTenantOwner => isTenantOwner ? of(isTenantOwner) :
                this.store.select(AuthSelectors.hasPermissionInRole(role, permission))
            )
        );
    }

   


    /**
     * Retrieves the access level for a specific document type.
     *
     * @param documentTypeId - The ID of the document type for which to retrieve the access level.
     * @returns An Observable that emits the access level for the specified document type.
     */
    documentAccessLevel(documentTypeId: number, categoryId:number | number[]):Observable<AccessLevelEnum> {
        return this.store.select(RolesSelectors.documentTypeAccessLevel(documentTypeId, categoryId))
    }

    

    /**
     * Retrieves the access level for a specific contract type.
     *
     * @param {number} contractTypeId - The ID of the contract type.
     * @param docTypeId - The ID of the document type.
     * @param categoryId - The ID of the category type.
     * @returns {Observable<AccessLevelEnum>} An observable that emits the access level for the specified contract type.
     */
    contractsAccessLevel(contractTypeId: number, docTypeId:number, categoryId:number | number[]):Observable<AccessLevelEnum> {
        return this.store.select((RolesSelectors.contractTypeAccessLevel)(contractTypeId, docTypeId, categoryId)).pipe(take(1));
    }

 
    
    
    /**
     * Checks if the user has all the specified permissions.
     *
     * @param permissions - An array of permissions to check.
     * @returns An Observable that emits `true` if the user has all the specified permissions, otherwise `false`.
     */
    hasAllPermissions(permissions: PermissionsEnum[]): Observable<boolean> {
        return combineLatest(permissions.map(x => this.hasPermission(x)))
            .pipe(map((x: boolean[]) => x.every(Boolean)));
    }



    /**
     * Retrieve information about given current logged user permission for contracts
     * @param templateAccessLevelId
     * @return AccessLevelEnum
     */
    templateAccessLevel(templateAccessLevelId: number, docTypeId:number, categoryIds:number | number[]):Observable<AccessLevelEnum> {
        return this.store.select((RolesSelectors.templateTypeAccessLevel)(templateAccessLevelId, docTypeId, categoryIds)).pipe(take(1));
    }




    /**
     * Checks if the user has full access to a document based on its type.
     *
     * This method first checks if the user is a tenant owner. If the user is a tenant owner,
     * it returns an observable emitting `true`. If the user is not a tenant owner, it checks
     * the user's access level for the specified document type and returns an observable emitting
     * `true` if the access level is `full_access`, otherwise `false`.
     *
     * @param documentTypeId - The ID of the document type to check access for.
     * @returns An observable emitting `true` if the user has full access to the document, otherwise `false`.
     */
    hasFullAccessToDocument(documentTypeId: number, categoryId:number | number[]): Observable<boolean> {
        return this.isTenantOwner$.pipe(
            switchMap(isTenantOwner => isTenantOwner === true
                ? of(isTenantOwner)
                : this.documentAccessLevel(documentTypeId, categoryId).pipe(map(x => x === AccessLevelEnum.full_access))
            ),
            shareReplay()
        );
    }


    // TODO: seems to be not used
    /**
     * If that user has permission of contracts
     * @param documentTypeId 
     * @returns 
     */
    hasFullAccessToContract(documentTypeId: number, docTypeId:number, categoryId:number | number[]) : Observable<boolean> {
        return this.isTenantOwner$.pipe(
            switchMap(isTenantOwner => isTenantOwner === true
                ? of(isTenantOwner)
                : this.contractsAccessLevel(documentTypeId, docTypeId, categoryId).pipe(map(x => {
                    return x === AccessLevelEnum.full_access
                }))
            ),
            shareReplay()
        );
    }


    /**
     * Creates a function that checks the access level for a given type and category.
     *
     * @param selector - A function that takes a type and category and returns a MemoizedSelector for AccessLevelEnum.
     * 
     * @todo We will not use this now cause categories related logic are not being used now in permissions
     * @returns A function that takes a typeId and categoryId and returns an Observable of AccessLevelEnum.
     */
    private accessLevelCheckFactoryWithCategory( selector: (type:number, category:number) =>  MemoizedSelector<object, AccessLevelEnum> ): (typeId: number, categoryId: number) => Observable<AccessLevelEnum>{
        return (typeId:number, categoryId:number)=> this.store.select(selector(typeId, categoryId))
            .pipe(take(1));
    }


    /**
     * Creates a function that checks the access level for a given type.
     *
     * @param selector - A function that takes a type and returns a MemoizedSelector for AccessLevelEnum.
     * 
     * @description This method creates a function that checks the access level for a given type.
     * @returns A function that takes a typeId and returns an Observable of AccessLevelEnum.
     */
    isNotRestricted(area: string): Observable<boolean> {
        return combineLatest([this.userRoles$, this.isTenantOwner$]).pipe(
            map(([roles, tenantOwner]) => {
              // If the tenantOwner is true, return true immediately
              if (tenantOwner) {
                return true;
              }
          
              // Proceed with checking the restrictions if tenantOwner is false
              let restrictionCount = 0;
          
              for (let i = 0; i < roles?.length; i++) {
                // Check if any permission matches the area
                if (roles[i].permissions.some(per => per.name === area)) {
                  restrictionCount++;
                }
              }          
              // If all roles have restrictions, return false
              return restrictionCount !== roles?.length;
            })
          );
    }


    
    /**
     * Checks if the given result was created by the current user.
     *
     * @template T - The type of the result object, which must include a `user_id` property.
     * @param {T} result - The result object to check.
     * @returns {Observable<boolean>} An observable that emits `true` if the result was created by the current user, otherwise `false`.
     */
    isCreatedByMe<T extends { user_id: number }>(result: T): Observable<boolean> {
        return this._userSettingsFacade$.currentUserId$.pipe(
            take(1),
            map(userId => userId === result?.user_id)
        );
    }

    canViewRoles(): Observable<boolean> {
        return this.hasSomePermission([
            PermissionsEnum.role_view,
            PermissionsEnum.role_edit,
            PermissionsEnum.role_delete
        ]);
    }

    canViewGroups(): Observable<boolean> {
        return this.hasSomePermission([
            PermissionsEnum.group_view,
            PermissionsEnum.group_edit,
            PermissionsEnum.group_delete
        ]);
    }

    canViewUsers(): Observable<boolean> {
        return this.hasSomePermission([
            PermissionsEnum.user_view,
            PermissionsEnum.user_edit_info,
            PermissionsEnum.user_edit_permissions,
            PermissionsEnum.user_manage_workspaces,
            PermissionsEnum.user_invite,
            PermissionsEnum.user_delete
        ]);
    }
}
